type SessionUser = {
  id?: number;
  username?: string;
  roles?: string[];
  adminAccess?: 'full' | 'shop_admin_orders' | 'shop_manager_orders' | 'intern_orders' | '';
};

let sessionRedirecting = false;

export const clearPosSession = (options: { redirect?: boolean } = {}) => {
  const shouldRedirect = options.redirect !== false;
  localStorage.removeItem('posAuth');
  localStorage.removeItem('posUser');
  localStorage.removeItem('posUserData');
  localStorage.removeItem('posPassword');
  localStorage.removeItem('wpUrl');
  localStorage.removeItem('posSessionExpiresAt');
  localStorage.removeItem('posAdminToken');
  localStorage.removeItem('posAdminAccess');
  window.dispatchEvent(new Event('pos-session-cleared'));
  if (shouldRedirect && !sessionRedirecting && window.location.pathname !== '/') {
    sessionRedirecting = true;
    window.location.assign('/');
  }
};

export const getPosSessionState = () => {
  const isAuthenticated = localStorage.getItem('posAuth') === 'true';
  const expiresAt = localStorage.getItem('posSessionExpiresAt');
  const expiresAtMs = expiresAt ? parseInt(expiresAt, 10) : NaN;
  const isExpired = Number.isFinite(expiresAtMs) ? Date.now() > expiresAtMs : true;
  const adminToken = localStorage.getItem('posAdminToken') || '';
  const adminAccess = localStorage.getItem('posAdminAccess') || '';
  const rawUser = localStorage.getItem('posUserData');
  let user: SessionUser | null = null;

  if (rawUser) {
    try {
      user = JSON.parse(rawUser);
    } catch {
      user = null;
    }
  }

  const derivedAdminAccess =
    adminAccess === 'full' || adminAccess === 'shop_admin_orders' || adminAccess === 'shop_manager_orders' || adminAccess === 'intern_orders'
      ? adminAccess
      : user?.roles?.includes('administrator')
        ? 'full'
        : user?.roles?.includes('shop_administrator')
          ? 'shop_admin_orders'
          : user?.roles?.includes('shop_manager')
            ? 'shop_manager_orders'
            : user?.roles?.includes('intern')
              ? 'intern_orders'
          : '';

  return {
    isAuthenticated,
    isExpired,
    expiresAtMs: Number.isFinite(expiresAtMs) ? expiresAtMs : null,
    adminToken,
    hasAdminToken: !!adminToken,
    isWooAdmin: !!user?.roles?.includes('administrator'),
    adminAccess: derivedAdminAccess,
    hasAnyAdminAccess: derivedAdminAccess === 'full' || derivedAdminAccess === 'shop_admin_orders' || derivedAdminAccess === 'shop_manager_orders' || derivedAdminAccess === 'intern_orders',
    hasFullAdminAccess: derivedAdminAccess === 'full',
    hasShopAdminOrdersAccess: derivedAdminAccess === 'shop_admin_orders',
    hasShopManagerOrdersAccess: derivedAdminAccess === 'shop_manager_orders',
    hasInternOrdersAccess: derivedAdminAccess === 'intern_orders',
    hasLimitedOrdersAccess: derivedAdminAccess === 'shop_admin_orders' || derivedAdminAccess === 'shop_manager_orders' || derivedAdminAccess === 'intern_orders',
    user,
  };
};

export const buildAdminFunctionHeaders = () => {
  const token = localStorage.getItem('posAdminToken') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

export const shouldClearAdminSession = (input: {
  status?: number;
  error?: unknown;
  message?: unknown;
}) => {
  const status = Number(input.status || 0);
  if (status === 401) return true;

  const combined = `${String(input.error || '')} ${String(input.message || '')}`.toLowerCase();
  return combined.includes('unauthorized') || combined.includes('invalid jwt');
};

type TrustedPosUser = {
  id?: number;
  username: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  roles?: string[];
  adminAccess?: 'full' | 'shop_admin_orders' | 'shop_manager_orders' | 'intern_orders' | '';
};

type TrustedPosSession = {
  user: TrustedPosUser;
  sessionToken: string;
  wpUrl?: string;
  dayKey: string;
  trustedAt: number;
  expiresAt: number;
};

const TRUSTED_POS_SESSIONS_KEY = 'posTrustedDailySessions';
export const POS_BOARD_ROUTE = '/';

export const getTbilisiDayKey = (date = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Tbilisi',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(date);

const getNextTbilisiMidnightMs = () => {
  const now = new Date();
  const dayKey = getTbilisiDayKey(now);
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1, 20, 0, 0)).getTime();
};

const trustedSessionKey = (user: Pick<TrustedPosUser, 'id' | 'username'>) => String(user.id || user.username || '').trim().toLowerCase();

export const getTrustedPosSessions = (): Record<string, TrustedPosSession> => {
  try {
    const parsed = JSON.parse(localStorage.getItem(TRUSTED_POS_SESSIONS_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object') return {};
    const dayKey = getTbilisiDayKey();
    const now = Date.now();
    const active = Object.fromEntries(Object.entries(parsed).filter(([, value]: any) => value?.dayKey === dayKey && Number(value?.expiresAt || 0) > now));
    if (Object.keys(active).length !== Object.keys(parsed).length) localStorage.setItem(TRUSTED_POS_SESSIONS_KEY, JSON.stringify(active));
    return active as Record<string, TrustedPosSession>;
  } catch {
    return {};
  }
};

export const getTrustedPosSession = (user: Pick<TrustedPosUser, 'id' | 'username'>) => getTrustedPosSessions()[trustedSessionKey(user)] || null;

export const saveTrustedPosSession = (input: { user: TrustedPosUser; sessionToken: string; wpUrl?: string }) => {
  const key = trustedSessionKey(input.user);
  if (!key) return;
  const sessions = getTrustedPosSessions();
  sessions[key] = {
    user: input.user,
    sessionToken: input.sessionToken || '',
    wpUrl: input.wpUrl || localStorage.getItem('wpUrl') || '',
    dayKey: getTbilisiDayKey(),
    trustedAt: Date.now(),
    expiresAt: getNextTbilisiMidnightMs(),
  };
  localStorage.setItem(TRUSTED_POS_SESSIONS_KEY, JSON.stringify(sessions));
};

export const activateTrustedPosSession = (session: TrustedPosSession) => {
  localStorage.setItem('posAuth', 'true');
  localStorage.setItem('posUser', session.user.username);
  localStorage.setItem('posUserData', JSON.stringify(session.user));
  localStorage.setItem('posAdminToken', session.sessionToken || '');
  localStorage.setItem('posAdminAccess', session.user.adminAccess || '');
  localStorage.setItem('posSessionExpiresAt', String(session.expiresAt));
  if (session.wpUrl) localStorage.setItem('wpUrl', session.wpUrl);
};

export const saveActivePosSessionAsTrusted = () => {
  const raw = localStorage.getItem('posUserData');
  if (!raw) return;
  try {
    const user = JSON.parse(raw);
    if (!user?.username) return;
    saveTrustedPosSession({ user, sessionToken: localStorage.getItem('posAdminToken') || '', wpUrl: localStorage.getItem('wpUrl') || '' });
  } catch {
    // ignore malformed user cache
  }
};

export const clearActivePosSessionOnly = () => {
  localStorage.removeItem('posAuth');
  localStorage.removeItem('posUser');
  localStorage.removeItem('posUserData');
  localStorage.removeItem('posPassword');
  localStorage.removeItem('wpUrl');
  localStorage.removeItem('posSessionExpiresAt');
  localStorage.removeItem('posAdminToken');
  localStorage.removeItem('posAdminAccess');
};

export const getPosWorkspaceKey = (username?: string | null) => `posWorkspace:${getTbilisiDayKey()}:${String(username || '').trim().toLowerCase()}`;
